import React, { useMemo } from "react";
import moment from "moment";
import { CompanyCompleteLedgerColumns } from "../../assets/Columns/CompanyCompleteLedgerColumns";
import { filterTableRowsBySearch } from "../../utils/tableSearch";
import { formatDateTime } from "../../utils/dateTime";

const formatRs = (n) =>
  `Rs ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

const formatDate = (row) => {
  const raw = row?.date;
  if (typeof raw === "number" && raw > 0) {
    return formatDateTime(raw);
  }
  if (row?.n_date && row.n_date !== "-") return row.n_date;
  return "—";
};

const isSummaryRow = (row) =>
  row?.invoice === "-" && row?.desc === "-" && row?.date === "-";

const buildCardGroups = (rows) => {
  const groups = [];
  for (const row of rows) {
    if (isSummaryRow(row)) {
      groups.push({ kind: "total", row });
      continue;
    }
    if (Number(row.dr) > 0 && row.name) {
      const key = `${row.invoice}-${row.date}`;
      const last = groups[groups.length - 1];
      if (last?.kind === "purchaseGroup" && last.key === key) {
        last.items.push(row);
        last.totalDr += Number(row.dr || 0);
        last.balance = row.balance;
      } else {
        groups.push({
          kind: "purchaseGroup",
          key,
          invoice: row.invoice,
          date: row.date,
          desc: row.desc,
          items: [row],
          totalDr: Number(row.dr || 0),
          balance: row.balance,
        });
      }
    } else {
      groups.push({ kind: "single", row });
    }
  }
  return groups;
};

const PurchaseGroupCard = ({ group }) => (
  <div className="rounded-xl border border-line bg-card p-4 shadow-soft">
    <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
      <span className="text-xs font-bold px-2.5 py-1 rounded-full border bg-red-100 text-red-800 border-red-200">
        Purchase (Bill)
      </span>
      {group.invoice && group.invoice !== "-" && (
        <span className="text-xs font-semibold text-muted">
          Inv #{group.invoice}
        </span>
      )}
    </div>

    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <p className="text-sm font-semibold text-ink">{formatDate(group)}</p>
      <p className="text-lg font-bold text-red-700">+ {formatRs(group.totalDr)}</p>
    </div>

    {group.desc && group.desc !== "-" && (
      <p className="text-sm text-inkSecondary mt-1">{group.desc}</p>
    )}

    <div className="mt-3 rounded-lg border border-line overflow-hidden">
      {group.items.map((item, i) => (
        <div
          key={`${item.name}-${i}`}
          className={`px-3 py-2.5 text-xs ${
            i > 0 ? "border-t border-line" : ""
          } bg-subtle`}
        >
          <p className="font-semibold text-ink">{item.name}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-muted">
            {Number(item.qty) > 0 && <span>Qty: {item.qty}</span>}
            {item.price != null && Number(item.price) > 0 && (
              <span>U Price: {formatRs(item.price)}</span>
            )}
            <span className="font-semibold text-red-600">{formatRs(item.dr)}</span>
          </div>
        </div>
      ))}
    </div>

    {group.balance != null && group.balance !== "-" && (
      <div className="mt-3 pt-3 border-t border-line flex justify-between items-center">
        <span className="text-xs text-muted">Balance</span>
        <span className="text-sm font-bold text-main">{formatRs(group.balance)}</span>
      </div>
    )}
  </div>
);

const PayableTxnCard = ({ row }) => {
  const isTotal = isSummaryRow(row);
  const isPayment = Number(row?.cr) > 0;

  return (
    <div
      className={`rounded-xl border bg-card p-4 shadow-soft ${
        isTotal ? "border-main bg-brandSoft" : "border-line"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <span
          className={`text-xs font-bold px-2.5 py-1 rounded-full border ${
            isPayment
              ? "bg-successSoft text-success border-success/20"
              : "bg-subtle text-inkSecondary border-line"
          }`}
        >
          {isPayment ? "Payment (Paid)" : isTotal ? "Total" : "Entry"}
        </span>
        {!isTotal && row.invoice && row.invoice !== "-" && (
          <span className="text-xs font-semibold text-muted">
            Inv #{row.invoice}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-ink">{formatDate(row)}</p>
        {!isTotal && isPayment && (
          <p className="text-lg font-bold text-green-700">- {formatRs(row.cr)}</p>
        )}
      </div>

      {row.desc && row.desc !== "-" && (
        <p className="text-sm text-inkSecondary mt-1">{row.desc}</p>
      )}

      {!isTotal && row.balance != null && row.balance !== "-" && (
        <div className="mt-3 pt-3 border-t border-line flex justify-between items-center">
          <span className="text-xs text-muted">Balance</span>
          <span className="text-sm font-bold text-main">{formatRs(row.balance)}</span>
        </div>
      )}

      {isTotal && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted">Total Paid (CR)</p>
            <p className="font-bold text-green-700">{formatRs(row.cr)}</p>
          </div>
          <div>
            <p className="text-xs text-muted">Total Bills (DR)</p>
            <p className="font-bold text-red-700">{formatRs(row.dr)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const PayableTxnCardList = ({ rows, searchText, columns }) => {
  const filtered = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return filterTableRowsBySearch(
      list,
      searchText,
      columns || CompanyCompleteLedgerColumns
    );
  }, [rows, searchText, columns]);

  const groups = useMemo(() => buildCardGroups(filtered), [filtered]);

  if (groups.length === 0) {
    return (
      <div className="text-center text-muted py-10 text-sm">
        No transactions found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group, i) => {
        if (group.kind === "purchaseGroup") {
          return <PurchaseGroupCard key={group.key} group={group} />;
        }
        if (group.kind === "total") {
          return <PayableTxnCard key={`total-${i}`} row={group.row} />;
        }
        return (
          <PayableTxnCard
            key={`${group.row.invoice}-${group.row.date}-${i}`}
            row={group.row}
          />
        );
      })}
    </div>
  );
};

export default PayableTxnCardList;
